"use client";

import { Tokens } from "~/services/api/types/tokens";
import { User } from "~/services/api/types/user";
import {
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    AuthActionsContext,
    AuthContext,
    AuthTokensContext,
    TokensInfo,
} from "./auth-context";
import Cookies from "js-cookie";
import useFetchBase from "~/services/api/use-fetch-base";
import { AUTH_LOGOUT_URL } from "~/services/api/config";
import { ALL_ME_RELATIONS, buildAuthMeUrl } from "~/services/api/services/auth";
import HTTP_CODES_ENUM from "../api/types/http-codes";
import { STORAGE_KEYS } from "~/types/Storage";
import { shutdown } from "~/lib/intercom/IntercomScript";
import useUserId from "~/hooks/useUserId";
import { removeStorageItem } from "~/lib/storage/Storage";
import { AUTH_TOKEN_COOKIE } from "./auth-cookie";

function AuthProvider(props: PropsWithChildren<object>) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<User | null>(null);
    const { setUserId, resetUserId } = useUserId();
    const tokensInfoRef = useRef<Tokens>({
        token: null,
        refreshToken: null,
        tokenExpires: null,
    });
    const fetchBase = useFetchBase();

    const setTokensInfoRef = useCallback((tokens: TokensInfo) => {
        tokensInfoRef.current = tokens ?? {
            token: null,
            refreshToken: null,
            tokenExpires: null,
        };
    }, []);

    const setTokensInfo = useCallback(
        (tokensInfo: TokensInfo) => {
            setTokensInfoRef(tokensInfo);

            if (tokensInfo) {
                Cookies.set(AUTH_TOKEN_COOKIE, JSON.stringify(tokensInfo), { expires: 7 });
            } else {
                Cookies.remove(AUTH_TOKEN_COOKIE);
                setUser(null);
            }
        },
        [setTokensInfoRef]
    );

    const logOut = useCallback(async () => {
        if (tokensInfoRef.current.token) {
            await fetchBase(
                AUTH_LOGOUT_URL,
                {
                    method: "POST",
                },
                {
                    token: tokensInfoRef.current.token,
                    refreshToken: tokensInfoRef.current.refreshToken,
                    tokenExpires: tokensInfoRef.current.tokenExpires,
                }
            );
        }
        setTokensInfo(null);
        removeStorageItem(STORAGE_KEYS.USER_ID);
        shutdown();
        resetUserId();
    }, [setTokensInfo, fetchBase, resetUserId]);

    const loadData = useCallback(async () => {
        const tokens = JSON.parse(
            Cookies.get(AUTH_TOKEN_COOKIE) ?? "null"
        ) as TokensInfo;
        setIsLoading(true);

        setTokensInfoRef(tokens);

        try {
            if (tokens?.token) {
                const response = await fetchBase(
                    buildAuthMeUrl(ALL_ME_RELATIONS),
                    {
                        method: "GET",
                    },
                    {
                        token: tokens.token,
                        refreshToken: tokens.refreshToken,
                        tokenExpires: tokens.tokenExpires,
                        setTokensInfo,
                    }
                );

                // A timed-out / offline / aborted request resolves to
                // `undefined` (the total fetch contract — see use-fetch-base.ts),
                // NOT to a `Response`. That is a transient network failure, not
                // an auth rejection: keep the session and just finish loading so
                // a momentary connection drop (e.g. a slow FB/IG webview on app
                // open) never silently logs the user out. Only an explicit 401
                // tears the session down.
                if (!response) {
                    return;
                }

                if (response.status === HTTP_CODES_ENUM.UNAUTHORIZED) {
                    logOut();
                    return;
                }

                const data = await response.json();
                setUser(data);
                setUserId(data.id);
            }
        } catch {
            logOut();
        }
        finally {
            setIsLoaded(true);
            setIsLoading(false);
        }
    }, [
        fetchBase,
        logOut,
        setTokensInfoRef,
        setTokensInfo,
        setUserId,
    ]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const contextValue = useMemo(
        () => ({
            isLoaded,
            isLoading,
            user,
        }),
        [isLoaded, isLoading, user]
    );

    const contextActionsValue = useMemo(
        () => ({
            setUser,
            logOut,
        }),
        [logOut]
    );

    const contextTokensValue = useMemo(
        () => ({
            tokensInfoRef,
            setTokensInfo,
        }),
        [setTokensInfo]
    );

    return (
        <AuthContext.Provider value={contextValue}>
            <AuthActionsContext.Provider value={contextActionsValue}>
                <AuthTokensContext.Provider value={contextTokensValue}>
                    {props.children}
                </AuthTokensContext.Provider>
            </AuthActionsContext.Provider>
        </AuthContext.Provider>
    );
}

export default AuthProvider;
